import React, {useRef, useState} from "react";
import {Link, useHistory} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {Form} from "@unform/web";
import InputMask from "../../../components/Form/InputMask";
import {InputAdornment} from "@material-ui/core";
import {IoMdCheckmark} from "react-icons/io";
import {BiArrowBack} from "react-icons/bi";
import CustomButton from "../../../components/Form/Button";
import * as Yup from "yup";
import {toast} from "react-toastify";
import messageTypes from "../../../constants/MessagesTypes";
import BankConfirmModal from "../../../components/Bank/BankConfirmModal";
import api from "../../../services/api";
import {currencyFormat} from "../../../utils/masks";
import {getBank} from "../../../services/Bank";
import {actions} from "../../../redux/actions";
import InputDate from "../../../components/Form/InputDate";
import {useTranslation} from "react-i18next";
import DivDiagonal from "../../../components/DivDiagonal";
import {
    TradingWrapper,
    ContentArea,
    PageHeader as TradingPageHeader,
    TradingCard,
} from "../../Trading/styles";
import {
    StyledFormWrapper,
    ButtonContainer,
    FormRow,
} from "../../Trading/New/styles";
import styled from "styled-components";

/** Igual /bank, /bank/view e /estrategias */
const BankFlowPageHeader = styled(TradingPageHeader)`
    z-index: 2;
    margin-bottom: 28px;

    @media (min-width: 768px) {
        margin-bottom: 36px;
    }
`;

const BankFlowNarrow = styled.div`
    max-width: 720px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    position: relative;
    z-index: 2;
    margin-top: 16px;

    @media (min-width: 576px) {
        margin-top: 20px;
    }

    @media (min-width: 992px) {
        margin-top: 24px;
    }
`;

const backBtnStyle = {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    color: '#e0e0e0',
    padding: '14px 28px',
    borderRadius: '10px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
};

const concludeRetireBtnStyle = {
    background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
    border: 'none',
    color: '#ffffff',
    padding: '14px 32px',
    borderRadius: '10px',
    fontWeight: 600,
    boxShadow: '0 4px 15px rgba(231, 76, 60, 0.35)',
    transition: 'all 0.3s ease',
};

export default function Retire() {
    const { coinType} = useSelector((state) => state.BankReducer);
    const formRef = useRef(null);
    const dispatch = useDispatch();
    const history = useHistory();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pending, setPending] = useState(null);
    const { t } = useTranslation();

    async function handleRetire(data) {
        try {
            formRef.current.setErrors({});
            const schema = Yup.object().shape({
                value: Yup.string().required(t('input_required')),
            });

            await schema.validate(data, {
                abortEarly: false,
            });

            setPending({
                value: data.value.replace(/[^0-9,-]*/g, '').replace(',', '.'),
                rate: (data.rate || '').replace(/[^0-9,-]*/g, '').replace(',', '.'),
                created: data.created,
            });
            setConfirmOpen(true);

        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                const validationErrors = {};
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
                toast.warning(messageTypes.REQUIRED_FIELDS);
                return;
            }

            toast.error(messageTypes.ERROR);
        }
    }

    function closeConfirmModal() {
        if (submitting) return;
        setConfirmOpen(false);
        setPending(null);
    }

    async function confirmRetire() {
        if (!pending) return;
        setSubmitting(true);
        try {
            const { value, created, rate } = pending;
            const q = new URLSearchParams({
                created: String(created),
                rate: String(rate ?? ''),
            }).toString();
            await api.post(`/api/bank/retire/${value}?${q}`, [], {
                headers: {
                    'Content-Type': 'application/json',
                    auth: true,
                },
            });
            toast.success(messageTypes.SUCCESS);
            const bankData = await getBank();
            dispatch(actions.updateBank(bankData));
            setConfirmOpen(false);
            setPending(null);
            history.push('/bank/movements');
        } catch (e) {
            toast.error(messageTypes.ERROR);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <TradingWrapper>
            <DivDiagonal />
            <ContentArea>
                <BankFlowPageHeader>
                    <h1>
                        <i className="fas fa-minus-circle" aria-hidden="true"/> {t('retire_card_title')}
                    </h1>
                </BankFlowPageHeader>

                <BankFlowNarrow>
                    <TradingCard>
                        <StyledFormWrapper>
                            <Form ref={formRef} onSubmit={handleRetire}>
                                <FormRow>
                                    <InputDate
                                        name="created"
                                        label={t('retire_label_input_date') + ' *'}
                                        value={new Date()}
                                    />
                                </FormRow>

                                <div className="row">
                                    <div className="col-12 col-md-6">
                                        <FormRow>
                                            <InputMask
                                                type="text"
                                                name="rate"
                                                label={t('retire_label_input_rate')}
                                                mask={'currency'}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">{coinType}</InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </FormRow>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <FormRow>
                                            <InputMask
                                                type="text"
                                                name="value"
                                                label={t('retire_label_input_value') + ' *'}
                                                mask={'currency'}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">{coinType}</InputAdornment>
                                                    ),
                                                }}
                                                autoFocus
                                            />
                                        </FormRow>
                                    </div>
                                </div>

                                <ButtonContainer>
                                    <Link to="/bank/movements" style={{ textDecoration: 'none' }}>
                                        <CustomButton
                                            variant="outlined"
                                            className="btn btn-secondary font-weight-bold text-capitalize mobile-button"
                                            startIcon={<BiArrowBack/>}
                                            style={backBtnStyle}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            {t('btn_back')}
                                        </CustomButton>
                                    </Link>
                                    <CustomButton
                                        className="btn font-weight-bold text-capitalize mobile-button"
                                        type="submit"
                                        startIcon={<IoMdCheckmark/>}
                                        style={concludeRetireBtnStyle}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 22px rgba(231, 76, 60, 0.45)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(231, 76, 60, 0.35)';
                                        }}
                                    >
                                        {t('btn_conclude')}
                                    </CustomButton>
                                </ButtonContainer>
                            </Form>
                        </StyledFormWrapper>
                    </TradingCard>
                </BankFlowNarrow>
            </ContentArea>

            <BankConfirmModal
                isOpen={confirmOpen && !!pending}
                onClose={closeConfirmModal}
                onConfirm={confirmRetire}
                variant="retire"
                loading={submitting}
                message={
                    pending
                        ? t('retire_msg_confirm', {
                              value: `${coinType} ${currencyFormat(pending.value)}`,
                          })
                        : ''
                }
            />
        </TradingWrapper>
    );
}
